<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { vOnClickOutside } from '@vueuse/components';

import { useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Compose from './Compose.vue';
import { MODAL_MODES, VIEW_TYPES, DRAFT_STATUSES } from './constants';

const store = useStore();
const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

const displayMode = ref(MODAL_MODES.MINIMIZED);
const dragging = ref(false);
const currentDraft = ref({});
const currentClientX = ref(0);
const currentDragOffset = ref(0);
const dragHandle = ref(null);
const mouseupHandler = ref(null);
const mousemoveHandler = ref(null);
const initLeftPosition = ref(
  uiSettings.value.draft_compose_minimized_position || '60%'
);

const minimizeComposeModal = () => {
  displayMode.value = MODAL_MODES.MINIMIZED;
  updateUISettings({ draft_compose_mode: MODAL_MODES.MINIMIZED });
};

const openComposeModal = () => {
  displayMode.value = MODAL_MODES.EDITING;
  updateUISettings({ draft_compose_mode: MODAL_MODES.EDITING });
};

const closeComposeModal = () => {
  displayMode.value = MODAL_MODES.CLOSED;
  updateUISettings({ draft_compose_mode: MODAL_MODES.CLOSED });
};

const loadCurrentDraft = async () => {
  currentDraft.value = {};
  if (uiSettings.value.editing_draft_id) {
    const draft = await store.dispatch(
      'drafts/getSingle',
      uiSettings.value.editing_draft_id
    );
    currentDraft.value = draft;
    if (!draft.id) {
      useAlert(t('DRAFTS.NOT_FOUND'));
      closeComposeModal();
    } else if (draft.status === DRAFT_STATUSES.DELIVERED) {
      useAlert(t('DRAFTS.ALREADY_SENT'));
      closeComposeModal();
    }
  } else {
    closeComposeModal();
  }
};

const loadDisplayMode = () => {
  displayMode.value =
    uiSettings.value.draft_compose_mode || MODAL_MODES.MINIMIZED;
};

const onDragHandleMouseDown = event => {
  dragging.value = true;
  document.body.style.userSelect = 'none';
  currentDragOffset.value =
    event.clientX - dragHandle.value.getBoundingClientRect().left;
};

const leftPosition = computed(() => {
  if (currentDragOffset.value === 0) {
    return initLeftPosition.value;
  }

  const safeAreaLeftPercent = 0.05;
  const safeAreaRightPercent = 0.95;
  const innerWidth = window.innerWidth;
  const offsetX = currentClientX.value - currentDragOffset.value;
  const dragElWidth = dragHandle.value
    ? dragHandle.value.getBoundingClientRect().width
    : 0;

  let leftPx = 0;
  if (offsetX < innerWidth * safeAreaLeftPercent) {
    leftPx = safeAreaLeftPercent * innerWidth;
  } else if (offsetX > innerWidth * safeAreaRightPercent - dragElWidth) {
    leftPx = innerWidth * safeAreaRightPercent - dragElWidth;
  } else {
    leftPx = offsetX;
  }
  return `${(leftPx / innerWidth) * 100}%`;
});

const shouldHide = computed(() => {
  if (!currentDraft.value.id) return true;
  return currentDraft.value.status === DRAFT_STATUSES.DELIVERED;
});

const setupMouseEvents = () => {
  mousemoveHandler.value = document.addEventListener('mousemove', event => {
    if (dragging.value) {
      currentClientX.value = event.clientX;
    }
  });
  mouseupHandler.value = document.addEventListener('mouseup', () => {
    if (dragging.value) {
      dragging.value = false;
      document.body.style.userSelect = 'auto';
      updateUISettings({
        draft_compose_minimized_position: leftPosition.value,
      });
    }
  });
};

onMounted(async () => {
  setupMouseEvents();
  loadCurrentDraft();
  loadDisplayMode();
});

onBeforeUnmount(() => {
  document.removeEventListener('mouseup', mouseupHandler);
  document.removeEventListener('mousemove', mousemoveHandler);
});

watch(
  () => uiSettings.value.editing_draft_id,
  () => {
    loadCurrentDraft();
  }
);

watch(
  () => uiSettings.value.draft_compose_mode,
  () => {
    loadDisplayMode();
  }
);
</script>

<template>
  <div v-if="shouldHide" />
  <div
    v-else-if="displayMode === MODAL_MODES.EDITING"
    class="fixed z-50 unset-0 top-0 left-0 w-screen h-screen bg-n-alpha-black1 dark:bg-n-alpha-white backdrop-blur-[4px] flex items-start justify-center p-6 pt-6"
  >
    <div
      v-on-click-outside="[minimizeComposeModal, { ignore: ['.modal-mask'] }]"
      class="rounded-lg shadow-lg w-full h-full overflow-hidden dark:bg-n-alpha-3 bg-white relative"
    >
      <NextButton
        v-tooltip.top="t('DRAFTS.MODAL.CLOSE_TOOLTIP')"
        link
        lg
        icon="i-lucide-x"
        class="absolute top-4 right-4 text-n-slate-11"
        @click="closeComposeModal"
      />
      <NextButton
        v-tooltip.top="t('DRAFTS.MODAL.MINIMIZE_TOOLTIP')"
        link
        lg
        icon="i-lucide-minus"
        class="absolute top-4 right-12 text-n-slate-11"
        @click="minimizeComposeModal"
      />
      <Compose
        v-if="currentDraft.id"
        :draft-id="currentDraft.id"
        :mode="VIEW_TYPES.MODAL"
      />
    </div>
  </div>
  <div
    v-if="displayMode === MODAL_MODES.MINIMIZED"
    ref="dragHandle"
    class="fixed z-50 bottom-0"
    :style="{ left: leftPosition }"
  >
    <div
      class="minimized-el rounded-t-lg shadow-lg flex gap-2 items-center px-4 py-1 h-10 hover:h-14 transition-all border border-b-0 border-n-slate-6 bg-n-alpha-3 dark:bg-n-alpha-white"
    >
      <NextButton
        v-tooltip.top="t('DRAFTS.MODAL.DRAG_HANDLE_TOOLTIP')"
        link
        lg
        icon="i-lucide-grip-vertical"
        class="me-1 text-n-slate-11 !cursor-ew-resize"
        @mousedown.prevent="onDragHandleMouseDown"
      />
      <NextButton
        v-tooltip.top="currentDraft.meta?.subject"
        link
        justify="start"
        class="text-sm text-n-slate-12 hover:!no-underline w-[15rem]"
        @click="openComposeModal"
      >
        <div class="w-full flex gap-0 flex-col items-start">
          <p class="mb-0 truncate w-full text-start">
            {{
              $t('DRAFTS.MODAL.MINIMIZE_LABEL', {
                subject: currentDraft.meta?.subject || `#${currentDraft.id}`,
              })
            }}
          </p>
          <p
            v-if="currentDraft.inbox"
            class="detail-info mb-0 text-start font-light text-n-slate-11 w-full truncate text-xs"
          >
            {{ currentDraft.inbox?.name }}
            {{
              currentDraft.contact?.name
                ? `to ${currentDraft.meta?.to_email}`
                : ''
            }}
          </p>
        </div>
      </NextButton>
      <NextButton
        v-tooltip.top="t('DRAFTS.MODAL.CLOSE_MGS')"
        link
        icon="i-lucide-x"
        class="ms-2"
        @click="closeComposeModal"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.detail-info {
  display: none;
}

.minimized-el:hover .detail-info {
  display: block;
}
</style>
